import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { InfoIcon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  InputFieldWithCurrency,
  SingleSelect,
  useCurrencies,
  usePermissions,
} from '@/modules/common';
import { CustomRadioGroup } from '@/modules/common/components/molecules/selectors/radio-group';
import {
  BaseFeatureTab,
  BaseTabProps,
  FeatureMode,
  FeatureTabRef,
  SubmitDateRange,
  useTabFormState,
} from '@/modules/common/components/templates/feature-details';
import { DEFAULT_CURRENCY_CODE } from '@/modules/common/constants/currency.constants';
import { useOrganizationDefaults } from '@/modules/common/hooks';
import { OpsiumPolicy } from '@/modules/common/types/opsium-policy';
import { RateMask } from '@/modules/common/utils/rate-mask';
import { ResolveConflictsModal } from '@/modules/projects/components/modals/resolve-conflicts-modal';
import { useCheckTeamMemberBillingConflicts } from '@/modules/team/hooks/billing/use-check-team-member-billing-conflicts';
import { useTeamMemberChangeConflictResolution } from '@/modules/team/hooks/use-team-member-change-conflict-resolution';
import {
  teamMemberBillingConflictFields,
  TeamMemberBillingFieldKey,
} from '@/modules/team/utils/change-conflicts/team-member-billing-conflict-config';
import { SettingsRoutes } from '@/router/routes';
import { compareFormValues } from '@/utils/form-state.utils';

import {
  InternalRateType,
  MemberStatus,
  TeamMember,
  TeamMemberFormData,
  TeamMemberTab,
} from '../../../types';
import {
  TeamMemberBillingFormData,
  teamMemberBillingSchema,
} from '../../../validations/team-member-billing-schema';

export interface TeamMemberBillingTabProps extends BaseTabProps<TeamMember, TeamMemberFormData> {
  /**
   * Callback to directly save data to API after conflict resolution
   */
  onDirectSave?: (
    data: TeamMemberFormData,
    effectiveStartDate?: Date,
    effectiveEndDate?: Date | null
  ) => Promise<void>;
  /**
   * Callback to navigate to another tab (goes through unsaved changes guard)
   */
  onTabChange?: (tab: string, additionalParams?: Record<string, string>) => void;
}

export interface TeamMemberBillingTabRef extends FeatureTabRef<TeamMemberFormData> {}

const TAG = 'TeamMemberBillingTab:';

/**
 * Team member billing and rate management component.
 * Handles internal rates, external rates, and currency configuration.
 */
export const TeamMemberBillingTab = forwardRef<TeamMemberBillingTabRef, TeamMemberBillingTabProps>(
  (props, ref) => {
    const {
      mode,
      featureData: teamMemberData,
      draftData,
      onDirectSave,
      onFormStateChange,
      onTabChange,
    } = props;
    const { t } = useTranslation(['authenticated.team', 'common']);

    // Check permissions early to use in form setup
    const { hasPermission } = usePermissions();
    const canManageExternalRate = hasPermission(OpsiumPolicy.ExternalRatesManage);
    const canManageInternalRate = hasPermission(OpsiumPolicy.InternalRatesManage);

    // Fetch currencies and organization defaults
    const { currencies, isLoadingCurrencies } = useCurrencies();

    const { data: organizationDefaults, isLoading: isLoadingOrgDefaults } =
      useOrganizationDefaults();

    const defaultCurrency = organizationDefaults?.currency.code;
    const isLoadingCurrencyData = isLoadingCurrencies || isLoadingOrgDefaults;

    const { checkConflicts, isPending: isCheckingConflicts } = useCheckTeamMemberBillingConflicts();

    /**
     * Builds initial form values based on mode and available data sources.
     */
    const initialValues = useMemo((): TeamMemberBillingFormData => {
      if (mode === FeatureMode.Create && draftData) {
        return {
          internalRateType: canManageInternalRate
            ? (draftData.internalRateType ?? InternalRateType.MonthlySalary)
            : undefined,
          internalRateCurrency: canManageInternalRate
            ? (draftData.internalRateCurrency ?? defaultCurrency ?? DEFAULT_CURRENCY_CODE)
            : undefined,
          internalRate: canManageInternalRate ? draftData.internalRate : undefined,
          externalHourlyRate: canManageExternalRate ? draftData.externalHourlyRate : undefined,
        };
      }

      if (mode === FeatureMode.Create) {
        return {
          internalRateType: canManageInternalRate ? InternalRateType.MonthlySalary : undefined,
          internalRateCurrency: canManageInternalRate
            ? (defaultCurrency ?? DEFAULT_CURRENCY_CODE)
            : undefined,
          internalRate: undefined,
          externalHourlyRate: undefined,
        };
      }

      // For edit/read mode
      return {
        internalRateType: canManageInternalRate
          ? (teamMemberData?.internalRateType ?? InternalRateType.MonthlySalary)
          : undefined,
        internalRateCurrency: canManageInternalRate
          ? (teamMemberData?.internalRateCurrency ?? defaultCurrency ?? DEFAULT_CURRENCY_CODE)
          : undefined,
        internalRate: canManageInternalRate ? teamMemberData?.internalRate : undefined,
        externalHourlyRate: canManageExternalRate ? teamMemberData?.externalHourlyRate : undefined,
      };
    }, [
      mode,
      teamMemberData,
      draftData,
      defaultCurrency,
      canManageInternalRate,
      canManageExternalRate,
    ]);

    const form = useForm<TeamMemberBillingFormData>({
      resolver: zodResolver(
        teamMemberBillingSchema(t, { requireInternalRate: canManageInternalRate })
      ),
      mode: 'onChange',
      defaultValues: initialValues,
    });

    // Reset form when initialValues change
    useEffect(() => {
      form.reset(initialValues);
    }, [form, initialValues]);

    // Watch rate type for dynamic suffix text
    const watchedRateType = form.watch('internalRateType');
    const watchedCurrency = form.watch('internalRateCurrency');

    // Create adapter function to convert to TeamMemberFormData
    const getFormDataAdapter = (): TeamMemberFormData => {
      const billingData = form.getValues();
      // Only return Billing tab specific fields - don't include fields from other tabs
      // This prevents overwriting data from Info/Capacity/Integrations tabs in Create mode
      return {
        internalRateCurrency: billingData.internalRateCurrency,
        internalRateType: billingData.internalRateType,
        internalRate:
          billingData.internalRate && RateMask.isMasked(billingData.internalRate)
            ? undefined
            : billingData.internalRate,
        externalHourlyRate:
          billingData.externalHourlyRate && RateMask.isMasked(billingData.externalHourlyRate)
            ? undefined
            : billingData.externalHourlyRate,
      } as TeamMemberFormData;
    };

    // Use the tab form state hook
    const {
      isReadMode,
      handleSubmit: baseHandleSubmit,
      getFormData,
    } = useTabFormState<TeamMember, TeamMemberBillingFormData>(props, {
      form,
      initialValues,
      isArchivedFn: member => member?.status === MemberStatus.Archived,
      triggerValidationOnMount: true,
      customDirtyStateFn: (currentValues, initialValues) => {
        if (mode === FeatureMode.Create) {
          return form.formState.isValid;
        } else {
          if (!form.formState.isValid) {
            return false;
          }
          return initialValues
            ? compareFormValues(currentValues, initialValues)
            : form.formState.isDirty;
        }
      },
    });

    /**
     * Creates form values in the format expected by the conflict resolution modal
     */
    const getFormValuesForConflicts = (): Record<TeamMemberBillingFieldKey, unknown> => {
      const formValues = form.getValues();
      return {
        internalRateType: formValues.internalRateType,
        internalRateCurrency: formValues.internalRateCurrency,
        internalRate: formValues.internalRate,
        externalHourlyRate: formValues.externalHourlyRate,
      };
    };

    // Use conflict resolution hook
    const {
      showConflictModal,
      conflictSnapshots,
      selectedDateRange,
      closeConflictModal,
      handleSubmitWithConflicts,
      handleResolveConflicts,
    } = useTeamMemberChangeConflictResolution<TeamMemberBillingFormData, TeamMemberBillingFieldKey>(
      {
        tag: TAG,
        form,
        teamMemberId: teamMemberData?.id ? String(teamMemberData.id) : undefined,
        checkConflicts,
        isCheckingConflicts,
        onFormStateChange,
        getFormData: getFormDataAdapter,
        onDirectSave,
        fields: teamMemberBillingConflictFields,
        currentFormValues: getFormValuesForConflicts(),
      }
    );

    /**
     * Submit handler with conflict detection
     */
    const handleSubmit = async (dateRange?: SubmitDateRange): Promise<void> => {
      // Skip conflict detection for Create mode
      if (mode === FeatureMode.Create) {
        await baseHandleSubmit();
        return;
      }

      // Use the conflict resolution hook for Edit/EditChange modes
      await handleSubmitWithConflicts(baseHandleSubmit, dateRange);
    };

    // Internal rate type options
    const rateTypeOptions = [
      {
        value: InternalRateType.MonthlySalary,
        label: t('details.tabs.billing.internalRate.rateType.monthlySalary'),
      },
      {
        value: InternalRateType.HourlyCostRate,
        label: t('details.tabs.billing.internalRate.rateType.hourlyCostRate'),
      },
    ];

    // Dynamic suffix text based on rate type
    const getInternalRateSuffix = () => {
      const currency = watchedCurrency || defaultCurrency || DEFAULT_CURRENCY_CODE;
      return watchedRateType === InternalRateType.MonthlySalary
        ? t('common:rateUnits.perMonth', { currency })
        : t('common:rateUnits.perHour', { currency });
    };

    const getExternalRateSuffix = () => {
      const currency = defaultCurrency || DEFAULT_CURRENCY_CODE;
      return t('common:rateUnits.perHour', { currency });
    };

    const [isRateInfoOpen, setIsRateInfoOpen] = useState(false);
    const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const handleRateInfoMouseEnter = () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
      setIsRateInfoOpen(true);
    };

    const handleRateInfoMouseLeave = () => {
      closeTimerRef.current = setTimeout(() => setIsRateInfoOpen(false), 100);
    };

    const tabContent = (
      <Form {...form}>
        <div className="space-y-4">
          {/* Internal Rate Block */}
          {canManageInternalRate && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
                <CardTitle>{t('details.tabs.billing.internalRate.title')}</CardTitle>
                {onTabChange && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onTabChange(TeamMemberTab.Changes, { changeType: 'billing' })}
                  >
                    {t('details.tabs.billing.internalRate.showChanges')}
                  </Button>
                )}
              </CardHeader>
              <CardContent className="space-y-4">
                <CustomRadioGroup
                  name="internalRateType"
                  control={form.control}
                  label={t('details.tabs.billing.internalRate.rateType.label')}
                  options={rateTypeOptions}
                  disabled={isReadMode}
                />

                <FormField
                  control={form.control}
                  name="internalRateCurrency"
                  render={({ field: _field }) => (
                    <FormItem>
                      <FormLabel>{t('details.tabs.billing.internalRate.currency')}</FormLabel>
                      <FormControl>
                        <SingleSelect
                          name="internalRateCurrency"
                          control={form.control}
                          options={currencies.map(currency => ({
                            value: currency.code,
                            label: currency.toString(),
                          }))}
                          placeholder={
                            isReadMode
                              ? t('common:notSelected')
                              : t('details.tabs.billing.internalRate.currencyPlaceholder')
                          }
                          disabled={isReadMode || isLoadingCurrencyData}
                          loading={isLoadingCurrencyData}
                          emptyMessage={
                            isLoadingCurrencyData
                              ? t('common:loading')
                              : t('details.tabs.billing.internalRate.noCurrenciesFound')
                          }
                          searchable={true}
                          isClearSelectionEnabled={false}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <InputFieldWithCurrency
                  name="internalRate"
                  label={t('details.tabs.billing.internalRate.rate')}
                  placeholder={t('details.tabs.billing.internalRate.ratePlaceholder')}
                  control={form.control}
                  suffixText={getInternalRateSuffix()}
                  maxDecimalPlaces={0}
                  isReadMode={isReadMode || isLoadingCurrencyData}
                />
              </CardContent>
            </Card>
          )}

          {/* External Rate Block */}
          {canManageExternalRate && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
                <CardTitle>{t('details.tabs.billing.externalRate.title')}</CardTitle>
                {onTabChange && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onTabChange(TeamMemberTab.Changes, { changeType: 'billing' })}
                  >
                    {t('details.tabs.billing.externalRate.showChanges')}
                  </Button>
                )}
              </CardHeader>
              <CardContent className="space-y-4">
                <Alert variant="info">
                  <InfoIcon className="h-4 w-4" />
                  <AlertDescription>
                    <Trans
                      i18nKey="details.tabs.billing.externalRate.alertMessage"
                      t={t}
                      components={{
                        bold: <span className="font-semibold"></span>,
                      }}
                    />
                  </AlertDescription>
                </Alert>

                <div className="flex items-end gap-2">
                  <div className="flex-1">
                    <InputFieldWithCurrency
                      name="externalHourlyRate"
                      label={t('details.tabs.billing.externalRate.hourlyRate')}
                      placeholder={t('details.tabs.billing.externalRate.hourlyRatePlaceholder')}
                      control={form.control}
                      suffixText={getExternalRateSuffix()}
                      maxDecimalPlaces={0}
                      isReadMode={isReadMode}
                    />
                  </div>
                  <Popover open={isRateInfoOpen}>
                    <PopoverTrigger asChild>
                      <InfoIcon
                        className="mb-2.5 h-4 w-4 shrink-0 cursor-help text-muted-foreground"
                        onMouseEnter={handleRateInfoMouseEnter}
                        onMouseLeave={handleRateInfoMouseLeave}
                      />
                    </PopoverTrigger>
                    <PopoverContent
                      className="max-w-xs bg-primary p-3 text-sm text-primary-foreground"
                      onMouseEnter={handleRateInfoMouseEnter}
                      onMouseLeave={handleRateInfoMouseLeave}
                      onOpenAutoFocus={e => e.preventDefault()}
                    >
                      <Trans
                        i18nKey="details.tabs.billing.externalRate.hourlyRateTooltip"
                        t={t}
                        components={{
                          settingsLink: (
                            <a
                              href={SettingsRoutes.Finance}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="underline"
                            />
                          ),
                        }}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </Form>
    );

    return (
      <>
        <BaseFeatureTab
          {...props}
          ref={ref}
          isArchivedFn={member => member?.status === MemberStatus.Archived}
          onSubmit={handleSubmit}
          isValid={form.formState.isValid}
          getFormData={getFormDataAdapter}
          isCheckingConflicts={isCheckingConflicts}
        >
          {tabContent}
        </BaseFeatureTab>

        {/* Conflict Resolution Modal */}
        {showConflictModal && selectedDateRange && (
          <ResolveConflictsModal
            isOpen={showConflictModal}
            onClose={closeConflictModal}
            snapshots={conflictSnapshots}
            currentFormValues={getFormValuesForConflicts()}
            selectedDateRange={selectedDateRange}
            fields={teamMemberBillingConflictFields}
            onResolve={handleResolveConflicts}
            t={t}
            translationNamespace="authenticated.team"
            context={{
              currency: currencies.find(c => c.code === watchedCurrency),
              currencies,
            }}
          />
        )}
      </>
    );
  }
);
